import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EntityStore } from '@igo2/common/entity';
import { MessageService } from '@igo2/core/message';
import { Feature, Layer, MeasureLengthUnit, SpatialFilterQueryType, SpatialFilterService, SpatialFilterType } from '@igo2/geo';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Constantes } from 'src/app/shared/constantes';
import { TranslateModule } from '@ngx-translate/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';

@Component({
    selector: 'app-sigt-spatial-filter-list',
    templateUrl: './sigt-spatial-filter-list.component.html',
    styleUrls: ['./sigt-spatial-filter-list.component.scss'],
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatAutocompleteModule,
        CommonModule,
        ReactiveFormsModule,
        TranslateModule
    ]
})
export class SigtSpatialFilterListComponent implements OnInit, OnDestroy {
  @Input()
  get store(): EntityStore<Feature> {
    return this._store;
  }
  set store(store: EntityStore<Feature>) {
    this._store = store;
  }
  private _store: EntityStore<Feature>;

  @Input()
  get queryType(): SpatialFilterQueryType {
    return this._queryType;
  }
  set queryType(queryType: SpatialFilterQueryType) {
    this.formControl.setValue('');
    this._queryType = queryType;
  }
  private _queryType: SpatialFilterQueryType;

  @Input()
  get zone() {
    return this._zone;
  }
  set zone(value) {
    this._zone = value;
    if (!value) {
      this.zoneWithBuffer = undefined;
      this.bufferFormControl.setValue(0);
    }
  }
  private _zone;

  @Input() layers: Layer[] = [];

  public zoneWithBuffer;
  public selectedZone: any;

  public measureUnit: MeasureLengthUnit = MeasureLengthUnit.Meters;

  public formControl = new UntypedFormControl();

  public bufferFormControl = new UntypedFormControl();

  /**
   * Available measure units for the measure type given
   * @internal
   */
  get measureUnits(): string[] {
    return [MeasureLengthUnit.Meters, MeasureLengthUnit.Kilometers];
  }

  @Output() zoneChange = new EventEmitter<Feature>();
  @Output() zoneWithBufferChange = new EventEmitter<Feature>();
  @Output() bufferChange = new EventEmitter<number>();
  @Output() measureUnitChange = new EventEmitter<MeasureLengthUnit>();

  formValueChanges$$: Subscription;
  bufferValueChanges$$: Subscription;
  messagesDeValidation: { [key: string]: string } = {};

  constructor(
    private spatialFilterService: SpatialFilterService,
    private messageService: MessageService,
  ) { }

  ngOnInit() {
    this.messagesDeValidation.limiteDeTampon = Constantes.MessageApplicatif.CompriseRayon_21A;

    this.formValueChanges$$ = this.formControl.valueChanges.subscribe(
      (value) => {
        if (value.length) {
          this.store.view.filter((feature) => {
            const filterNormalized = value
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');
            const featureNameNormalized = feature.properties.nom
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');
            return featureNameNormalized.includes(filterNormalized);
          });
        }
      }
    );

    this.bufferValueChanges$$ = this.bufferFormControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        let zoneToBuffer = this.selectedZone;
        let filterTypeToBufferBy = SpatialFilterType.Predefined;
        if (this.queryType as any === 'DirectionRegionalMRNF') {
          filterTypeToBufferBy = SpatialFilterType.Polygon;
          zoneToBuffer = Object.assign(this.selectedZone.geometry, {
            "meta": {
              "title": "Zone"
            },
            "properties": {
              "nom": "Zone",
              "type": this.selectedZone.geometry.type
            }
          });
        }
        if (
          this.measureUnit === MeasureLengthUnit.Meters &&
          value > 0 &&
          value <= 100000
        ) {
          this.bufferChange.emit(value);
          this.spatialFilterService
            .loadBufferGeometry(
              zoneToBuffer,
              filterTypeToBufferBy,
              value,
              this.queryType
            )
            .subscribe((featureGeom: Feature) => {
              this.zoneWithBuffer = featureGeom;
              this.zoneWithBufferChange.emit(this.zoneWithBuffer);
            });
        } else if (
          this.measureUnit === MeasureLengthUnit.Kilometers &&
          value > 0 &&
          value <= 100
        ) {
          this.bufferChange.emit(value);
          this.spatialFilterService
            .loadBufferGeometry(
              zoneToBuffer,
              filterTypeToBufferBy,
              value * 1000,
              this.queryType
            )
            .subscribe((featureGeom: Feature) => {
              this.zoneWithBuffer = featureGeom;
              this.zoneWithBufferChange.emit(this.zoneWithBuffer);
            });
        } else if (value === 0 && this.layers.length > 0) {
          this.bufferChange.emit(value);
          this.zoneWithBufferChange.emit(this.selectedZone);
        } else if (value < 0) {
          this.bufferFormControl.setValue(0);
          this.messageService.alert(
            this.messagesDeValidation.limiteDeTampon,
            'igo.geo.spatialFilter.warning'
          );
        } else if ((this.measureUnit === MeasureLengthUnit.Meters && value > 100000) ||
          (this.measureUnit === MeasureLengthUnit.Kilometers && value > 100)) {
          this.bufferFormControl.setValue(this.measureUnit === MeasureLengthUnit.Kilometers ? 100 : 100000);
          this.messageService.alert(
            this.messagesDeValidation.limiteDeTampon,
            'igo.geo.spatialFilter.warning'
          );
        }
      });
  }

  ngOnDestroy() {
    this.formValueChanges$$.unsubscribe();
  }

  displayFn(feature?: Feature): string | undefined {
    return feature ? feature.properties.nom : undefined;
  }

  onZoneChange(feature) {
    if (feature && this.queryType) {
      this.spatialFilterService
        .loadItemById(feature, this.queryType)
        .subscribe((featureGeom: Feature) => {
          this.selectedZone = featureGeom;
          this.zoneChange.emit(featureGeom);
        });
    }
  }

  /**
   * Set the measure unit
   * @internal
   */
  onMeasureUnitChange(unit: MeasureLengthUnit) {
    if (unit === this.measureUnit) {
      return;
    } else {
      this.measureUnit = unit;
      this.measureUnitChange.emit(this.measureUnit);
      this.measureUnit === MeasureLengthUnit.Meters
        ? this.bufferFormControl.setValue(this.bufferFormControl.value * 1000)
        : this.bufferFormControl.setValue(this.bufferFormControl.value / 1000);
    }
  }
}
